import React, { useEffect, useState, useRef } from 'react';

interface FloatingVideoPreviewProps {
  isVisible: boolean;
  position: { x: number; y: number };
  videoUrl: string;
}

const FloatingVideoPreview: React.FC<FloatingVideoPreviewProps> = ({
  isVisible,
  position,
  videoUrl
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!videoUrl) return;
    
    setIsLoaded(false);
    
    if (videoRef.current) {
      videoRef.current.src = videoUrl;
      
      const handleCanPlay = () => {
        setIsLoaded(true);
      };
      
      videoRef.current.addEventListener('canplay', handleCanPlay);
      
      // Check if video is already ready (e.g., cached)
      if (videoRef.current.readyState >= 3) {
        handleCanPlay();
      }
      
      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('canplay', handleCanPlay);
        }
      };
    }
  }, [videoUrl]);

  // Play/pause based on visibility
  useEffect(() => {
    if (videoRef.current) {
      if (isVisible && isLoaded) {
        videoRef.current.play().catch(err => {
          console.warn('Could not autoplay video preview:', err);
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [isVisible, isLoaded]);

  // Calculate position ensuring the preview stays within screen boundaries
  const calculateSafePosition = () => {
    const videoWidth = 440; // Width of the video container
    const videoHeight = 300; // Height of the video container
    
    // Calculate window boundaries
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Calculate horizontal position considering container width
    let xPos = position.x - 240; // Default position (left of the button)
    
    // Make sure left edge doesn't go off screen
    xPos = Math.max(xPos, videoWidth / 2);
    
    // Make sure right edge doesn't go off screen
    xPos = Math.min(xPos, windowWidth - videoWidth / 2);
    
    // Calculate vertical position
    let yPos = position.y;
    
    // Make sure top edge doesn't go off screen
    yPos = Math.max(yPos, videoHeight / 2);
    
    // Make sure bottom edge doesn't go off screen
    yPos = Math.min(yPos, windowHeight - videoHeight / 2);
    
    return {
      left: `${xPos}px`,
      top: `${yPos}px`
    };
  };

  const styles = calculateSafePosition();

  if (!videoUrl || !isVisible) return null;

  return (
    <div 
      ref={previewRef}
      className={`floating-video-preview ${isVisible && isLoaded ? 'visible' : ''}`}
      style={styles}
    >
      <video
        ref={videoRef}
        muted
        loop
        playsInline
        preload="auto"
        style={{ 
          opacity: isLoaded ? 1 : 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center center'
        }}
      />
      {!isLoaded && (
        <div className="video-loading-indicator" />
      )}
    </div>
  );
};

export default FloatingVideoPreview; 